import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AIAction, AIConflict, AIProposal } from '../api/types';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { actionLabels, displayValue, fieldLabels, outcomeLabels } from './format';

const reasonLabels: Record<string, string> = {
  AUTO_FILL: 'Leeres Feld',
  HIGH_CONFIDENCE: 'Hohe Sicherheit',
  CONFLICT_WITH_EXISTING: 'Widerspricht vorhandenem Wert',
  SUGGESTION: 'Vorschlag',
  LOW_CONFIDENCE: 'Unsicher – bitte prüfen',
  ADDRESS_NOT_CONFIRMED: 'Adresse nicht bestätigt',
  DIFFERS_FROM_APPOINTMENT: 'Weicht vom Termin ab',
  APPLY_FAILED: 'Automatische Übernahme nicht möglich',
};

interface Props {
  leadId?: string;
  // onCreateAppointment öffnet die Terminmaske mit den erkannten Angaben (Aktion CREATE_APPOINTMENT).
  onCreateAppointment?: (action: AIAction) => void;
  compact?: boolean;
  contactOnly?: boolean;
}

// AIReviewPanel zeigt KI-Vorschläge als Diff (alt → neu) mit Übernehmen/Verwerfen. Kritische
// Felder werden nie ungefragt geändert; automatische Übernahmen erscheinen in der Chronik.
export function AIReviewPanel({ leadId, onCreateAppointment, compact, contactOnly }: Props) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const reviews = useQuery({
    queryKey: projectKeys.aiReviews(project.id, leadId),
    queryFn: ({ signal }) => papi.aiReviews(leadId, signal),
    enabled: can('ai.review'),
    refetchInterval: 15_000,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
  const decide = useMutation({
    mutationFn: (d: { kind: 'proposal' | 'action'; id: string; accept: boolean }): Promise<unknown> =>
      d.kind === 'proposal'
        ? d.accept ? papi.acceptProposal(d.id) : papi.rejectProposal(d.id)
        : d.accept ? papi.acceptAction(d.id) : papi.rejectAction(d.id),
    onSuccess: invalidate,
  });
  const resolve = useMutation({
    mutationFn: (d: { callId: string; resolution: 'KEEP_MANUAL' | 'ACCEPT_AI' }) => papi.resolveAnalysis(d.callId, d.resolution),
    onSuccess: invalidate,
  });
  if (!can('ai.review') || !reviews.data) return null;
  const proposals = contactOnly ? reviews.data.proposals.filter((p) => p.entity_type === 'LEAD') : reviews.data.proposals;
  const actions = contactOnly ? [] : reviews.data.actions;
  const conflicts = contactOnly ? [] : reviews.data.conflicts;
  const relevantConflicts = leadId ? conflicts.filter((c) => c.lead_id === leadId) : conflicts;
  if (!proposals.length && !actions.length && !relevantConflicts.length) {
    return compact ? null : <p className="muted">Keine offenen KI-Prüfungen.</p>;
  }
  const canAccept = can('ai.accept_proposal');
  const canReject = can('ai.reject_proposal');

  return (
    <section className="card ai-review" aria-label="KI-Vorschläge">
      <h2>KI-Vorschläge</h2>
      {relevantConflicts.map((c: AIConflict) => (
        <div key={c.analysis_id} className="alert alert-warn">
          <strong>Widerspruch:</strong> {c.note}
          {!leadId && c.lead_name && <span className="muted"> · {c.lead_name}</span>}
          {canAccept && (
            <div className="form-actions">
              <button type="button" className="btn btn-small" disabled={resolve.isPending} onClick={() => resolve.mutate({ callId: c.call_id, resolution: 'KEEP_MANUAL' })}>
                Mitarbeiter-Ergebnis behalten
              </button>
              <button type="button" className="btn btn-small" disabled={resolve.isPending} onClick={() => resolve.mutate({ callId: c.call_id, resolution: 'ACCEPT_AI' })}>
                KI-Ergebnis übernehmen ({outcomeLabels[c.ai_outcome as keyof typeof outcomeLabels] ?? c.ai_outcome})
              </button>
            </div>
          )}
        </div>
      ))}
      <ul className="proposal-list">
        {proposals.map((p: AIProposal) => (
          <li key={p.id} className="proposal">
            <div>
              <strong>{fieldLabels[p.field_name] ?? p.field_name}</strong>
              {!leadId && p.lead_name && <span className="muted"> · {p.lead_name}</span>}
              <div className="diff">
                <span className="diff-old">{displayValue(p.old_value)}</span>
                <span aria-hidden="true">→</span>
                <span className="diff-new">{displayValue(p.proposed_value)}</span>
              </div>
              <small className="muted">
                Sicherheit {Math.round(p.confidence * 100)} % · {reasonLabels[p.reason ?? ''] ?? p.reason}
              </small>
            </div>
            <div className="proposal-actions">
              {canAccept && (
                <button type="button" className="btn btn-small btn-primary" disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'proposal', id: p.id, accept: true })}>
                  Übernehmen
                </button>
              )}
              {canReject && (
                <button type="button" className="btn btn-small" disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'proposal', id: p.id, accept: false })}>
                  Verwerfen
                </button>
              )}
            </div>
          </li>
        ))}
        {actions.map((a: AIAction) => (
          <li key={a.id} className="proposal">
            <div>
              <strong>{actionLabels[a.action_type] ?? a.action_type}</strong>
              {!leadId && a.lead_name && <span className="muted"> · {a.lead_name}</span>}
              <div className="muted">{a.reason}</div>
              {typeof a.payload.due_at === 'string' && <div>Zeitpunkt: {displayValue(a.payload.due_at)}</div>}
              {typeof a.payload.start_at === 'string' && <div>Termin: {displayValue(a.payload.start_at)}</div>}
            </div>
            <div className="proposal-actions">
              {canAccept && a.action_type === 'CREATE_APPOINTMENT' && onCreateAppointment && (
                <button type="button" className="btn btn-small btn-primary" onClick={() => onCreateAppointment(a)}>
                  Terminmaske öffnen
                </button>
              )}
              {canAccept && a.action_type !== 'CREATE_APPOINTMENT' && (
                <button type="button" className="btn btn-small btn-primary" disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'action', id: a.id, accept: true })}>
                  {a.action_type === 'REQUEST_REVIEW' ? 'Geprüft' : 'Ausführen'}
                </button>
              )}
              {canReject && (
                <button type="button" className="btn btn-small" disabled={decide.isPending} onClick={() => decide.mutate({ kind: 'action', id: a.id, accept: false })}>
                  Verwerfen
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {(decide.isError || resolve.isError) && <MutationError error={decide.error ?? resolve.error} />}
    </section>
  );
}
